import { useEffect, useState } from 'react';
import { useFeatureFlagEnabled } from '@posthog/react';
import posthog from 'posthog-js';

import { type FeatureFlagKey } from './featureFlags';
import { isPostHogEnabled } from './initPostHog';

export type FeatureFlagState = 'loading' | 'enabled' | 'disabled';

/** PostHog boolean flag as `'loading' | 'enabled' | 'disabled'`. See `docs/posthog-feature-flags.md`. */
export function useFeatureFlagState(flagKey: FeatureFlagKey): FeatureFlagState {
  const isEnabled = useFeatureFlagEnabled(flagKey);
  const flagsLoaded = useFeatureFlagsLoaded();
  const hasMounted = useHasMounted();

  if (!hasMounted) {
    return 'loading';
  }

  if (!isPostHogEnabled()) {
    return import.meta.env.DEV ? 'enabled' : 'disabled';
  }

  if (!flagsLoaded) {
    return 'loading';
  }

  return isEnabled ? 'enabled' : 'disabled';
}

function useHasMounted(): boolean {
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  return hasMounted;
}

function useFeatureFlagsLoaded(): boolean {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!isPostHogEnabled()) {
      return;
    }

    if (posthog.featureFlags?.hasLoadedFlags) {
      setLoaded(true);
      return;
    }

    return posthog.onFeatureFlags(() => setLoaded(true));
  }, []);

  return loaded;
}
